import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import { useSubjects } from './hooks/useSubjects';
import { SubjectDialog, TopicDialog, SubjectsList } from './components';
import { SubjectsPageProps } from './types';

export default function SubjectsPage({ isDark }: SubjectsPageProps) {
  const {
    subjects,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    topicsBySubject,
    expandedSubjectId,
    isTopicDialogOpen,
    editingTopic,
    topicFormData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    setIsTopicDialogOpen,
    setTopicFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleTopics,
    handleOpenCreateTopic,
    handleEditTopic,
    handleSaveTopic,
    handleDeleteTopic,
  } = useSubjects();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            Subjects
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Manage subjects and their topics
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
        >
          <Plus className="w-4 h-4 mr-2" /> New Subject
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden p-2',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <SubjectsList
          isDark={isDark}
          subjects={subjects}
          isLoading={isLoading}
          topicsBySubject={topicsBySubject}
          expandedSubjectId={expandedSubjectId}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleTopics={handleToggleTopics}
          onCreateTopic={handleOpenCreateTopic}
          onEditTopic={handleEditTopic}
          onDeleteTopic={handleDeleteTopic}
        />
      </Card>

      <SubjectDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        title="Create subject"
        description="Add a new subject"
        submitLabel="Create"
      />

      <SubjectDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        title="Edit subject"
        description="Update subject information"
        submitLabel="Save changes"
        isEdit
      />

      <TopicDialog
        isDark={isDark}
        isOpen={isTopicDialogOpen}
        isEdit={!!editingTopic}
        onOpenChange={setIsTopicDialogOpen}
        formData={topicFormData}
        onFormDataChange={setTopicFormData}
        onSubmit={handleSaveTopic}
      />
    </div>
  );
}
